// Input: smart-tap (single click → auto-move) AND drag-and-drop.
//
// Tap behavior (when the pointer barely moves):
//   - Tap any movable card → auto-move to the best valid destination.
//   - Tap stock → draw / recycle.
//
// Drag behavior (when the pointer moves past a small threshold):
//   - Drag any face-up tableau slice → drop on a destination pile.
//   - Drag waste/foundation top → drop on a destination pile.

import type { Card, GameState, Move, PileId } from "../game/types.ts";
import { applyMove, canMove, drawFromStock } from "../game/moves.ts";
import { pileKey } from "./render.ts";

const DRAG_THRESHOLD_PX = 6;
const AUTO_MOVE_ELEVATION_MS = 220;
const INVALID_TAP_SHAKE_MS = 430;
const INVALID_TAP_SECOND_BUZZ_MS = 210;
const invalidTapRestoreZ = new Map<HTMLElement, string>();
const invalidTapAnimations = new Map<HTMLElement, Animation>();

export interface InputBindings {
  onChange: (move?: Move) => void;
  onCancel?: () => void;
  onDraw?: () => void;
  onInvalidTap?: () => void;
}

interface SmartMoveResult {
  cardIds: number[];
  move: Move;
}

interface DragState {
  source: PileId;
  // Where in the pile the user actually pressed — needed by tap fallback so
  // a tap on a buried face-up card targets THAT card (and the run above it),
  // not the topmost card of the pile.
  pressIndex: number;
  pressPileSize: number;
  cardIds: number[];
  pointerId: number;
  startX: number;
  startY: number;
  offsets: Array<{ id: number; baseTransform: string }>;
  dragging: boolean;
}

export function attachInput(
  board: HTMLElement,
  state: { current: GameState | null },
  bindings: InputBindings,
): () => void {
  let drag: DragState | null = null;

  const findHitElement = (
    clientX: number,
    clientY: number,
    hideCardIds: number[],
  ): HTMLElement | null => {
    // Hide the dragging cards so elementFromPoint sees what's behind them.
    const cards = hideCardIds
      .map((id) =>
        board.querySelector<HTMLElement>(`.card[data-card-id="${id}"]`),
      )
      .filter((el): el is HTMLElement => !!el);
    const previousVisibility = cards.map((el) => el.style.pointerEvents);
    cards.forEach((el) => (el.style.pointerEvents = "none"));
    const hit = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    cards.forEach((el, i) => (el.style.pointerEvents = previousVisibility[i] ?? ""));
    return hit;
  };

  const onPointerDown = (ev: PointerEvent) => {
    const s = state.current;
    if (!s) return;
    if (ev.button !== 0 && ev.pointerType === "mouse") return;
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const cardEl = target.closest<HTMLElement>(".card");
    if (!cardEl) return;
    if (cardEl.classList.contains("placeholder")) return;

    const pile = parsePile(cardEl.dataset.pile ?? "");
    if (!pile) return;
    const indexInPile = Number(cardEl.dataset.pileIndex ?? "0");
    const pileSize = Number(cardEl.dataset.pileSize ?? "0");

    // Stock cards aren't draggable; they only respond to taps.
    if (pile.kind === "stock") return;

    const slice = candidateSlice(s, pile, indexInPile, pileSize);
    if (!slice) return;

    drag = {
      source: pile,
      pressIndex: indexInPile,
      pressPileSize: pileSize,
      cardIds: slice.map((c) => c.id),
      pointerId: ev.pointerId,
      startX: ev.clientX,
      startY: ev.clientY,
      offsets: slice.map((c) => {
        const el = board.querySelector<HTMLElement>(`.card[data-card-id="${c.id}"]`);
        return { id: c.id, baseTransform: el?.style.transform ?? "" };
      }),
      dragging: false,
    };
    cardEl.setPointerCapture(ev.pointerId);
  };

  const onPointerMove = (ev: PointerEvent) => {
    if (!drag || ev.pointerId !== drag.pointerId) return;
    const dx = ev.clientX - drag.startX;
    const dy = ev.clientY - drag.startY;
    if (!drag.dragging) {
      if (Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) return;
      drag.dragging = true;
      // Mark all dragging cards visually
      drag.cardIds.forEach((id) => {
        const el = board.querySelector<HTMLElement>(`.card[data-card-id="${id}"]`);
        if (el) {
          el.classList.add("dragging");
          el.style.zIndex = "9999";
        }
      });
    }
    drag.offsets.forEach(({ id, baseTransform }) => {
      const el = board.querySelector<HTMLElement>(`.card[data-card-id="${id}"]`);
      if (!el) return;
      el.style.transform = `${baseTransform} translate(${dx}px, ${dy}px)`;
    });
  };

  const onPointerUp = (ev: PointerEvent) => {
    const s = state.current;
    const d = drag;
    if (!d || ev.pointerId !== d.pointerId) return;
    drag = null;

    const removeDraggingClass = () => {
      d.cardIds.forEach((id) => {
        const el = board.querySelector<HTMLElement>(`.card[data-card-id="${id}"]`);
        if (el) el.classList.remove("dragging");
      });
    };

    if (!s) {
      removeDraggingClass();
      return;
    }

    // If the pointer barely moved, treat as a tap (smart move).
    if (!d.dragging) {
      removeDraggingClass();
      handleTap(board, s, d.source, d.pressIndex, d.pressPileSize, bindings);
      return;
    }

    const hit = findHitElement(ev.clientX, ev.clientY, d.cardIds);
    const dest = resolveDropTarget(hit);
    if (dest) {
      const move: Move = { from: d.source, to: dest, count: d.cardIds.length };
      if (canMove(s, move) && applyMove(s, move)) {
        // Valid drop — render immediately so the move is reflected.
        removeDraggingClass();
        bindings.onChange(move);
        return;
      }
    }

    // Invalid drop — animate the cards back to their original positions while
    // keeping the inline z-index = "9999" (set during pointermove) on top.
    // We DELAY the rerender until the snap-back transition completes so that
    // render's z-index reset doesn't yank the card behind its neighbors mid-flight.
    d.offsets.forEach(({ id, baseTransform }) => {
      const el = board.querySelector<HTMLElement>(`.card[data-card-id="${id}"]`);
      if (!el) return;
      el.classList.remove("dragging"); // re-enable CSS transition
      el.style.transform = baseTransform; // triggers transition back to start
      // Leave el.style.zIndex = "9999" — render will reset it after the timeout.
    });
    window.setTimeout(() => {
      (bindings.onCancel ?? bindings.onChange)();
    }, 220);
  };

  const onClick = (ev: Event) => {
    // Stock placeholder taps still work — `pointerdown` early-returned for stock,
    // so we get a real click here. Cards are handled in pointerup via dragging flag.
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const placeholderEl = target.closest<HTMLElement>(".card.placeholder");
    if (placeholderEl) {
      const s = state.current;
      if (!s) return;
      const key = placeholderEl.dataset.placeholder;
      if (key === pileKey({ kind: "stock", index: 0 })) {
        if (drawFromStock(s)) {
          bindings.onDraw?.();
          bindings.onChange();
        }
      }
      return;
    }
  };

  // Stock cards are tap-only (early-return in pointerdown), so route their click here.
  const onStockClick = (ev: Event) => {
    const s = state.current;
    if (!s) return;
    const target = ev.target as HTMLElement | null;
    const cardEl = target?.closest<HTMLElement>(".card");
    if (!cardEl || cardEl.classList.contains("placeholder")) return;
    const pile = parsePile(cardEl.dataset.pile ?? "");
    if (pile?.kind !== "stock") return;
    if (drawFromStock(s)) {
      bindings.onDraw?.();
      bindings.onChange();
    }
  };

  board.addEventListener("pointerdown", onPointerDown);
  board.addEventListener("pointermove", onPointerMove);
  board.addEventListener("pointerup", onPointerUp);
  board.addEventListener("pointercancel", onPointerUp);
  board.addEventListener("click", onClick);
  board.addEventListener("click", onStockClick);
  return () => {
    board.removeEventListener("pointerdown", onPointerDown);
    board.removeEventListener("pointermove", onPointerMove);
    board.removeEventListener("pointerup", onPointerUp);
    board.removeEventListener("pointercancel", onPointerUp);
    board.removeEventListener("click", onClick);
    board.removeEventListener("click", onStockClick);
  };
}

function parsePile(s: string): PileId | null {
  const [kind, idx] = s.split(":");
  if (
    kind !== "tableau" &&
    kind !== "foundation" &&
    kind !== "stock" &&
    kind !== "waste"
  )
    return null;
  const index = Number(idx);
  return { kind, index: Number.isFinite(index) ? index : 0 };
}

// Returns the slice of cards that would be picked up if the user grabs `pile[indexInPile]`.
function candidateSlice(
  s: GameState,
  pile: PileId,
  indexInPile: number,
  pileSize: number,
): Card[] | null {
  if (pile.kind === "tableau") {
    const col = s.tableau[pile.index];
    if (!col) return null;
    const card = col[indexInPile];
    if (!card || !card.faceUp) return null;
    return col.slice(indexInPile);
  }
  const isTop = indexInPile === pileSize - 1;
  if (!isTop) return null;
  if (pile.kind === "waste") {
    const top = s.waste[s.waste.length - 1];
    return top ? [top] : null;
  }
  if (pile.kind === "foundation") {
    const f = s.foundation[pile.index];
    const top = f && f[f.length - 1];
    return top ? [top] : null;
  }
  return null;
}

function resolveDropTarget(hit: HTMLElement | null): PileId | null {
  if (!hit) return null;
  // Drop on a placeholder
  const ph = hit.closest<HTMLElement>(".card.placeholder");
  if (ph) {
    const key = ph.dataset.placeholder;
    if (key) return parsePile(key);
  }
  // Drop on an existing card → use its pile
  const card = hit.closest<HTMLElement>(".card");
  if (card) {
    const pile = parsePile(card.dataset.pile ?? "");
    if (pile) return pile;
  }
  return null;
}

// Smart-click logic preserved as fallback when a drag is essentially a tap.
// Uses the EXACT card the player pressed — including buried face-up cards
// in a tableau column — so taps on a run's bottom card move that run.
function handleTap(
  board: HTMLElement,
  s: GameState,
  source: PileId,
  pressIndex: number,
  pressPileSize: number,
  bindings: InputBindings,
): void {
  if (pressPileSize === 0) return;
  const result = trySmartMove(s, source, pressIndex, pressPileSize);
  if (!result) {
    const cardIds = tapCandidateCardIds(s, source, pressIndex, pressPileSize);
    if (cardIds.length > 0) animateInvalidTap(board, cardIds, bindings);
    return;
  }
  bindings.onChange(result.move);
  elevateAutoMovedCards(board, result.cardIds, bindings);
}

function tapCandidateCardIds(
  s: GameState,
  source: PileId,
  indexInPile: number,
  pileSize: number,
): number[] {
  const isTopOfPile = indexInPile === pileSize - 1;
  if (source.kind === "tableau") {
    const col = s.tableau[source.index];
    if (!col) return [];
    const card = col[indexInPile];
    if (!card || !card.faceUp) return [];
    return col.slice(indexInPile).map((c) => c.id);
  }
  if (source.kind === "waste") {
    if (!isTopOfPile) return [];
    const top = s.waste[s.waste.length - 1];
    return top ? [top.id] : [];
  }
  if (source.kind === "foundation") {
    if (!isTopOfPile) return [];
    const pile = s.foundation[source.index];
    const top = pile?.[pile.length - 1];
    return top ? [top.id] : [];
  }
  return [];
}

function trySmartMove(
  s: GameState,
  source: PileId,
  indexInPile: number,
  pileSize: number,
): SmartMoveResult | null {
  const isTopOfPile = indexInPile === pileSize - 1;
  const count = pileSize - indexInPile;
  let movingCardIds: number[];

  if (source.kind === "tableau") {
    const col = s.tableau[source.index];
    if (!col) return null;
    const card = col[indexInPile];
    if (!card || !card.faceUp) return null;
    movingCardIds = col.slice(indexInPile).map((c) => c.id);
  } else if (source.kind === "waste") {
    if (!isTopOfPile) return null;
    const top = s.waste[s.waste.length - 1];
    if (!top) return null;
    movingCardIds = [top.id];
  } else if (source.kind === "foundation") {
    if (!isTopOfPile) return null;
    const pile = s.foundation[source.index];
    const top = pile?.[pile.length - 1];
    if (!top) return null;
    movingCardIds = [top.id];
  } else {
    return null;
  }

  const tryFoundation = count === 1 && source.kind !== "foundation";
  if (tryFoundation) {
    for (let f = 0; f < s.foundation.length; f++) {
      const move: Move = { from: source, to: { kind: "foundation", index: f }, count: 1 };
      if (canMove(s, move) && applyMove(s, move)) {
        return { cardIds: movingCardIds, move };
      }
    }
  }

  const dests: number[] = [];
  for (let t = 0; t < s.tableau.length; t++) {
    if (source.kind === "tableau" && t === source.index) continue;
    const col = s.tableau[t]!;
    if (col.length > 0) dests.push(t);
  }
  for (let t = 0; t < s.tableau.length; t++) {
    if (source.kind === "tableau" && t === source.index) continue;
    const col = s.tableau[t]!;
    if (col.length === 0) dests.push(t);
  }
  for (const t of dests) {
    const move: Move = { from: source, to: { kind: "tableau", index: t }, count };
    if (canMove(s, move) && applyMove(s, move)) {
      return { cardIds: movingCardIds, move };
    }
  }
  return null;
}

function elevateAutoMovedCards(
  board: HTMLElement,
  cardIds: number[],
  bindings: InputBindings,
): void {
  cardIds.forEach((id, idx) => {
    const el = board.querySelector<HTMLElement>(`.card[data-card-id="${id}"]`);
    if (el) el.style.zIndex = String(9999 + idx);
  });
  window.setTimeout(() => {
    (bindings.onCancel ?? bindings.onChange)();
  }, AUTO_MOVE_ELEVATION_MS);
}

function animateInvalidTap(
  board: HTMLElement,
  cardIds: number[],
  bindings: InputBindings,
): void {
  bindings.onInvalidTap?.();
  window.setTimeout(() => bindings.onInvalidTap?.(), INVALID_TAP_SECOND_BUZZ_MS);

  cardIds.forEach((id, idx) => {
    const el = board.querySelector<HTMLElement>(`.card[data-card-id="${id}"]`);
    if (!el) return;
    const baseTransform = el.style.transform || "translate(0px, 0px)";
    const restoreZ = invalidTapRestoreZ.get(el) ?? el.style.zIndex;
    invalidTapRestoreZ.set(el, restoreZ);
    el.getAnimations().forEach((animation) => animation.cancel());
    el.dataset.invalidTap = "1";
    el.style.zIndex = String(9999 + idx);
    const animation = el.animate(
      [
        { transform: baseTransform },
        { transform: `${baseTransform} translateX(-8px)` },
        { transform: `${baseTransform} translateX(8px)` },
        { transform: baseTransform },
        { transform: `${baseTransform} translateX(-5px)` },
        { transform: `${baseTransform} translateX(5px)` },
        { transform: baseTransform },
      ],
      {
        duration: INVALID_TAP_SHAKE_MS,
        easing: "ease-in-out",
      },
    );
    invalidTapAnimations.set(el, animation);
    const restore = () => {
      if (invalidTapAnimations.get(el) !== animation) return;
      el.style.zIndex = invalidTapRestoreZ.get(el) ?? "";
      delete el.dataset.invalidTap;
      invalidTapAnimations.delete(el);
      invalidTapRestoreZ.delete(el);
    };
    animation.addEventListener("finish", restore, { once: true });
    animation.addEventListener("cancel", restore, { once: true });
  });
}
